"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence } from "motion/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { loadThemeComponent } from "@/core/config/theme-component-loader";
import { useDictionary } from "@/core/hooks/use-dictionary";
import { requestOtp } from "@/core/lib/api/main/auth";

const NumberStepLoginForm = loadThemeComponent("NumberStepLoginForm");
const PasswordStepLoginForm = loadThemeComponent("PasswordStepLoginForm");
const OtpVerificationLoginStep = loadThemeComponent("OtpVerificationLoginStep");

type Step = 1 | 2 | 3;

interface LoginFlowProps {
    captcha_active: boolean;
    logo?: string;
    siteTitle?: string;
    loginBanner?: string;
}

export default function LoginFlow({ captcha_active, logo, siteTitle, loginBanner }: LoginFlowProps) {
    const { dictionary } = useDictionary();
    const [stepHistory, setStepHistory] = useState<Step[]>([1]);
    const step = stepHistory[stepHistory.length - 1];

    // بک‌اند فعلاً برای این فیلد گاهی به‌جای URL کامل، ID یا مقدار خالی
    // برمی‌گردونه؛ تا وقتی endpoint عمومی درست بشه، فقط وقتی مقدار واقعاً
    // یه آدرس http(s) معتبره تصویر رو نشون می‌دیم تا next/image کرش نکنه.
    const isValidImageUrl = (value?: string) => {
        if (!value) return false;
        try {
            const url = new URL(value);
            return url.protocol === "http:" || url.protocol === "https:";
        } catch {
            return false;
        }
    };
    const hasLoginBanner = isValidImageUrl(loginBanner);

    const [mobile, setMobile] = useState("");
    const [sessionToken, setSessionToken] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [timeLeft, setTimeLeft] = useState(60);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (step !== 3 || timeLeft <= 0) return;
        const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
        return () => clearInterval(timer);
    }, [step, timeLeft]);

    const handleResend = async () => {
        try {
            setIsLoading(true);
            await requestOtp(mobile, sessionToken);
            setTimeLeft(120);
        } catch (error) {
            console.error("Resend OTP error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const goBack = () => {
        if (stepHistory.length > 1) {
            setStepHistory((prev) => prev.slice(0, -1));
        }
    };

    const goToStep = (nextStep: Step) => {
        setStepHistory((prev) => [...prev, nextStep]);
    };

    const stepTitle: Record<Step, string | undefined> = {
        1: dictionary.forms?.login && dictionary.forms?.signin
            ? `${dictionary.forms.login}/${dictionary.forms.signin}`
            : dictionary.forms?.login,
        2: dictionary.forms?.password,
        3: dictionary.forms?.otpLogin,
    };

    // فرآیند واقعاً دو مرحله‌ای‌ست: «شماره موبایل» و بعد «تایید هویت»
    // (چه با رمز عبور، چه با کد یکبار مصرف)، برای همین نشانگر پیشرفت
    // هم فقط همین دو ایستگاه رو نشون می‌ده، نه سه‌تا.
    const progressStage = step === 1 ? 0 : 1;

    return (
        <div className="grid md:grid-cols-2 min-h-[calc(100vh-6rem)]">

            {/* Right — form panel */}
            <div className="flex flex-col bg-primary-50 px-6 sm:px-12 lg:px-20 py-8 sm:py-10">
                <div className="flex items-center justify-between">

                    <Link
                        href="/"
                        className="group flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-primary-600 hover:text-primary-900 transition-colors"
                    >
                        <ChevronLeft className="size-3.5 rtl:rotate-180 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5" />
                        <span className="relative">
                            {dictionary.ui?.pages?.returnToStore}
                            <span className="absolute inset-x-0 -bottom-0.5 h-px bg-current scale-x-100 origin-right transition-transform" />
                        </span>

                    </Link>
                </div>

                <div className="flex-1 flex items-center justify-center py-10">
                    <div className="w-full max-w-sm">

                        {/* نشانگر پیشرفت: دو ایستگاه، «شماره موبایل» و «تایید هویت» */}
                        <div className="flex items-center justify-center gap-2 mb-8" aria-hidden>
                            {[0, 1].map((stage) => (
                                <span key={stage} className="flex items-center gap-2">
                                    <span
                                        className={`size-1.5 rounded-full transition-colors duration-500 ${stage <= progressStage ? "bg-primary-900" : "bg-primary-300"
                                            }`}
                                    />
                                    {stage === 0 && (
                                        <span
                                            className={`block h-px w-8 transition-colors duration-500 ${progressStage >= 1 ? "bg-primary-900" : "bg-primary-300"
                                                }`}
                                        />
                                    )}
                                </span>
                            ))}
                        </div>

                        <div className="text-center mb-8 space-y-3">
                            {step !== 1 && (
                                <button
                                    type="button"
                                    onClick={goBack}
                                    className="cursor-pointer mb-1 text-[11px] uppercase tracking-[0.14em] text-primary-500 hover:text-primary-900 transition-colors"
                                >
                                    {dictionary.common?.back}
                                </button>
                            )}
                            <h1 className="text-2xl lg:text-3xl font-light uppercase tracking-tight text-primary-900">
                                {stepTitle[step]}
                            </h1>
                            {step === 1 && (
                                <p className="text-[13px] text-primary-500 leading-6 pt-1 max-w-[280px] mx-auto">
                                    {dictionary.ui?.pages?.loginDescription}
                                </p>
                            )}
                        </div>

                        {/* کارت: فرم رو از بک‌گراند صفحه جدا و برجسته می‌کنه */}
                        <div className="bg-primary-0 border border-primary-200/70 rounded-2xl shadow-[0_24px_60px_-32px_rgba(0,0,0,0.28)] p-6 sm:p-8">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={step}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {step === 1 && (
                                        <NumberStepLoginForm
                                            captcha_active={captcha_active}
                                            setMobile={setMobile}
                                            setSessionToken={setSessionToken}
                                            setRememberMe={setRememberMe}
                                            mobile={mobile}
                                            rememberMe={rememberMe}
                                            setStep={goToStep}
                                        />
                                    )}
                                    {step === 2 && (
                                        <PasswordStepLoginForm
                                            handleResend={handleResend}
                                            mobile={mobile}
                                            session_token={sessionToken}
                                            setStep={goToStep}
                                            isLoading={isLoading}
                                        />
                                    )}
                                    {step === 3 && (
                                        <OtpVerificationLoginStep
                                            mobile={mobile}
                                            session_token={sessionToken}
                                            handleResend={handleResend}
                                            timeLeft={timeLeft}
                                            setTimeLeft={setTimeLeft}
                                            goBack={goBack}
                                            setStep={goToStep}
                                        />
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </div>


                    </div>
                </div>
            </div>

            {/* Left — visual panel: اگه تصویر (loginBanner) از تنظیمات ست شده
                باشه همون نمایش داده می‌شه، وگرنه طرح تایپوگرافیک پیش‌فرض */}
            {hasLoginBanner ? (
                <div className="hidden md:flex relative items-center justify-center overflow-hidden">
                    <Image
                        src={loginBanner as string}
                        alt={siteTitle || "Login"}
                        fill
                        priority
                        className="object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-950/55 via-primary-950/5 to-primary-950/20" />


                </div>
            ) : (
                <div className="hidden md:flex relative bg-primary-900 text-primary-50 items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,color-mix(in_srgb,var(--color-primary-700)_35%,transparent),transparent_60%)]" />

                    <span
                        aria-hidden
                        className="absolute select-none whitespace-nowrap font-light tracking-tight text-primary-50/[0.06] text-[24vw] leading-none"
                    >
                        {siteTitle || "Lino"}
                    </span>

                    <div className="absolute top-10 inset-x-10 flex items-center justify-between">
                        <span className="block w-8 h-px bg-primary-50/60" />
                        <span className="text-[11px] uppercase tracking-[0.16em] text-primary-50/60">
                            {siteTitle}
                        </span>
                    </div>

                    <div className="relative z-10 px-16 text-center space-y-6">
                        <span className="block w-10 h-px bg-primary-50/70 mx-auto" />
                        <p className="text-[13px] leading-8 text-primary-50/70 max-w-xs mx-auto">
                            {dictionary.ui?.pages?.loginDescription}
                        </p>
                    </div>

                    <div className="absolute bottom-10 inset-x-10 text-[11px] uppercase tracking-[0.16em] text-primary-50/40">
                        {siteTitle}
                    </div>
                </div>
            )}
        </div>
    );
}